import { AccountsState } from '../types'

export interface OrphanReport {
  orphanedClassrooms: string[] // classroom IDs with missing owner
  orphanedCoTeachers: Array<{ classroomId: string; missingTeacherId: string }>
  orphanedLearners: Array<{ classroomId: string; missingLearnerId: string }>
  totalIssues: number
}

export function detectOrphanedClassrooms(state: AccountsState): OrphanReport {
  const report: OrphanReport = {
    orphanedClassrooms: [],
    orphanedCoTeachers: [],
    orphanedLearners: [],
    totalIssues: 0,
  }

  for (const [classroomId, classroom] of Object.entries(state.classrooms)) {
    // 1. Missing owner
    if (!state.accountsById[classroom.teacherId]) {
      report.orphanedClassrooms.push(classroomId)
      report.totalIssues++
    }

    // 2. Missing co-teachers
    if (classroom.coTeacherIds) {
      for (const coTeacherId of classroom.coTeacherIds) {
        if (!state.accountsById[coTeacherId]) {
          report.orphanedCoTeachers.push({
            classroomId,
            missingTeacherId: coTeacherId,
          })
          report.totalIssues++
        }
      }
    }

    // 3. Missing learners
    for (const learnerId of classroom.learnerIds) {
      if (!state.accountsById[learnerId]) {
        report.orphanedLearners.push({
          classroomId,
          missingLearnerId: learnerId,
        })
        report.totalIssues++
      }
    }
  }

  return report
}

export function cleanupOrphans(
  state: AccountsState,
  report: OrphanReport,
): AccountsState {
  let nextState = { ...state }

  // 1. Delete orphaned classrooms
  for (const classroomId of report.orphanedClassrooms) {
    const { [classroomId]: _, ...rest } = nextState.classrooms
    nextState.classrooms = rest
  }

  // 2. Remove missing co-teachers and learners from remaining classrooms
  for (const [classroomId, classroom] of Object.entries(nextState.classrooms)) {
    let updated = false
    let newCoTeacherIds = classroom.coTeacherIds
    let newLearnerIds = classroom.learnerIds

    const missingCoTeachers = report.orphanedCoTeachers.filter(
      (o) => o.classroomId === classroomId,
    )
    if (missingCoTeachers.length > 0 && newCoTeacherIds) {
      const missingIds = missingCoTeachers.map((o) => o.missingTeacherId)
      newCoTeacherIds = newCoTeacherIds.filter((id) => !missingIds.includes(id))
      updated = true
    }

    const missingLearners = report.orphanedLearners.filter(
      (o) => o.classroomId === classroomId,
    )
    if (missingLearners.length > 0) {
      const missingIds = missingLearners.map((o) => o.missingLearnerId)
      newLearnerIds = newLearnerIds.filter((id) => !missingIds.includes(id))
      updated = true
    }

    if (updated) {
      nextState.classrooms = {
        ...nextState.classrooms,
        [classroomId]: {
          ...classroom,
          coTeacherIds: newCoTeacherIds,
          learnerIds: newLearnerIds,
        },
      }
    }
  }

  return nextState
}
